import React, { useState, useEffect } from 'react';
import { Box, Grid, CircularProgress, Alert } from '@mui/material';
import {
  DateRangeSelector,
  UsageMetricCard,
  UsageByEndpointChart,
  RefreshButton,
  LoadingOverlay,
  UsageSkeleton,
} from '../Components';
import { useApiData } from '../hooks/useApiData';
import { formatUsd } from '../utils/formatUsd';

const ApiUsagePage = () => {
  const [dateRange, setDateRange] = useState('30');
  const {
    usageData,
    loading,
    updatingUsage,
    refreshing,
    error,
    updateUsage,
    refreshData,
  } = useApiData();

  // Calculate period based on selected date range
  const getPeriod = (days: string) => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - parseInt(days));

    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    };

    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  // Fetch usage data when date range changes
  useEffect(() => {
    updateUsage(parseInt(dateRange));
  }, [dateRange]);

  // Calculate metrics from API data
  const period = getPeriod(dateRange);
  const totalCost = usageData ? formatUsd(usageData.total_cost) : formatUsd(0);
  const totalApiCalls = usageData?.total_usage_count?.toString() || '0';

  if (loading) {
    return <UsageSkeleton />;
  }

  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <RefreshButton onRefresh={refreshData} loading={refreshing} />
      </Box>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      <Grid container spacing={3}>
        {/* Date Range Selector */}
        <Grid item xs={12} md={4}>
          <DateRangeSelector value={dateRange} onChange={setDateRange} />
        </Grid>

        {/* Metric Cards Row */}
        <Grid item xs={12}>
          <LoadingOverlay loading={updatingUsage}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <UsageMetricCard title="Period" value={period} />
              </Grid>
              <Grid item xs={12} md={3}>
                <UsageMetricCard title="Total Cost" value={totalCost} />
              </Grid>
              <Grid item xs={12} md={3}>
                <UsageMetricCard title="Total API Calls" value={totalApiCalls} />
              </Grid>
            </Grid>
          </LoadingOverlay>
        </Grid>

        {/* Usage by Endpoint Chart */}
        <Grid item xs={12}>
          <LoadingOverlay loading={updatingUsage}>
            <UsageByEndpointChart
              data={
                usageData || {
                  grouped_usage_data: {},
                  total_usage_count: 0,
                  total_cost: 0,
                }
              }
            />
          </LoadingOverlay>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ApiUsagePage;
